import React, { PropsWithChildren } from 'react'
import { Flex, Text, Icon, Button } from '@workwhile/ui'
import { Link } from 'react-router-dom'
import { ColumnDef } from '@tanstack/react-table'
import { formatInTimeZone } from 'date-fns-tz'
import { getLocalTimezone } from 'lib/time'
import { DayforceWebhooks } from 'api/admin/dayforce/listDayforceWebhooks'
import { useDayforceWebhooksContextValue } from '../../context/DayforceWebhooksProvider'
import { DayforceWebhookEventTypes } from 'pages/Dayforce/constants'
import { ExternalLinkIcon } from 'lucide-react'
import * as ROUTES from 'constants/routes'
import { PaginatedTable } from 'components/Table/PaginatedTable'

const getJobId = (payload: { event: string; data: any }) => {
  if (
    ['job_invitation', 'job_updated', 'job_canceled'].includes(payload.event)
  ) {
    return payload.data?.job?.id
  }

  if (
    [
      'worker_clocked_in',
      'worker_clocked_out',
      'worker_clock_out_allowed',
      'worker_ncns',
      'shift_updated',
    ].includes(payload.event)
  ) {
    return payload.data?.shift?.job_id
  }

  return null
}

const JobButton = ({
  agencyId,
  jobId,
  children,
}: PropsWithChildren<{ agencyId: string; jobId: string }>) => {
  const {
    state: { agencies },
  } = useDayforceWebhooksContextValue()

  const agencyBaseUrl = agencies?.[agencyId]?.dayforceBaseUrl

  if (!agencyBaseUrl) {
    return null
  }

  return (
    <Button
      kind="text"
      as="a"
      href={`${agencyBaseUrl}/jobs/${jobId}`}
      target="_blank"
      rel="noopener noreferrer"
      style={{ textDecoration: 'none', color: 'inherit' }}
      variant="secondary"
    >
      <Flex alignItems="center" gap={2}>
        {children}
        <Icon icon={ExternalLinkIcon} size={12} />
      </Flex>
    </Button>
  )
}

const WebhookDetailsButton = ({
  webhookId,
  children,
}: PropsWithChildren<{ webhookId: number }>) => (
  <Button
    as={Link}
    to={ROUTES.DAYFORCE_WEBHOOK_DETAIL.replace(
      ':webhookId',
      webhookId.toString()
    )}
    kind="text"
    variant="secondary"
  >
    {children}
  </Button>
)

const AgencyCell = ({ agencyId }: { agencyId: string }) => {
  const {
    state: { agencies },
  } = useDayforceWebhooksContextValue()

  return (
    <Text style={{ whiteSpace: 'nowrap' }}>
      {agencies?.[agencyId]?.name || agencyId}
    </Text>
  )
}

const columns: ColumnDef<DayforceWebhooks[number]>[] = [
  {
    header: 'ID',
    accessorKey: 'id',
    size: 80,
    cell: ({ row }) => (
      <Link
        to={ROUTES.DAYFORCE_WEBHOOK_DETAIL.replace(
          ':webhookId',
          String(row.original.id)
        )}
        style={{ color: 'inherit' }}
      >
        <Text as="span">{row.original.id}</Text>
      </Link>
    ),
  },
  {
    header: 'Agency',
    accessorKey: 'agencyId',
    size: 160,
    cell: ({ row }) => <AgencyCell agencyId={row.original.agencyId} />,
  },
  {
    header: 'Status',
    accessorKey: 'status',
    size: 120,
  },
  {
    header: 'Attempts',
    accessorKey: 'attempts',
    size: 100,
  },
  {
    header: 'Event',
    cell: ({ row }) => {
      const payload = JSON.parse(row.original.payload || '{}')
      if (!payload.event) {
        return <Text>Unknown</Text>
      }
      return (
        <Text style={{ whiteSpace: 'nowrap' }}>
          {DayforceWebhookEventTypes?.[payload.event] || payload.event}
        </Text>
      )
    },
  },
  {
    header: 'Created',
    accessorKey: 'createdAt',
    size: 220,
    cell: ({ row }) => (
      <Text style={{ whiteSpace: 'nowrap' }}>
        {formatInTimeZone(
          row.original.createdAt,
          getLocalTimezone(),
          'MMM dd, yyyy, hh:mm a zz'
        )}
      </Text>
    ),
  },
  {
    header: 'Actions',
    cell: ({ row }) => {
      const buttons: React.ReactNode[] = []

      const payload = JSON.parse(row.original.payload || '{}')
      if (payload?.event) {
        const jobId = getJobId(payload)
        if (jobId) {
          buttons.push(
            <JobButton agencyId={row.original.agencyId} jobId={jobId}>
              View job
            </JobButton>
          )
        }
      }

      return (
        <Flex gap={2} flexWrap="wrap">
          {buttons.map((button) => button)}
          <WebhookDetailsButton
            webhookId={row.original.id}
            key={row.original.id}
          >
            View details
          </WebhookDetailsButton>
        </Flex>
      )
    },
  },
]

export const DayforceWebhooksTable = () => {
  const {
    state: { data, pagination, isFetching },
    actions: { setPagination },
  } = useDayforceWebhooksContextValue()

  return (
    <PaginatedTable
      columns={columns}
      items={data.items}
      rowCount={data.totalRows}
      pagination={pagination}
      setPagination={setPagination}
      isFetching={isFetching}
      emptyMessage="No data found."
    />
  )
}
